import { ShipInfo } from '@ais-ui/websockets';
import {
  isCurrentPortCall,
  isNextPortPrediction,
  NextPortArrivalWithDate,
  useNextportShipImoGet,
  usePortGet,
} from '@ais-ui/api';
import { useSettings } from '../../Settings/SettingsProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  LoadingSpinner,
  Progress,
  Separator,
} from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PiClock,
  PiDotsThreeVertical,
  PiMagnifyingGlass,
  PiPath,
} from 'react-icons/pi';
import { cn, locodeToCountry } from '@ais-ui/utils';
import { EmptyListTable } from '../../../components/EmptyListTable';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { useTimeUntil } from '../../conversions/useTimeUntil';
import {
  shapeToVectorLayer,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { GeoJson, Geometry } from '@ais-ui/api-spec/shapes';
import { PortExtended } from '@ais-ui/api-spec/portserver';
import { fromLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import {
  formatProbability,
  probabilityDotClass,
} from '../../utils/probabilityFormatting';
import { useDrawRouteToPort } from '../../utils/useDrawRouteToPort';
import { NextPortNoPredictionCard } from './NextPortNoPredictionCard';
import { resolveNoPredictionReason } from './nextPortNoPrediction';

interface NextPortProps {
  shipInfo: ShipInfo;
}

interface NextPortItemProps {
  nextport: NextPortArrivalWithDate;
  shipInfo: ShipInfo;
  setPorts: React.Dispatch<React.SetStateAction<PortExtended[]>>;
}

/**
 * Resolves the destination port for a next-port arrival record and syncs it
 * into the shared `ports` state so the parent can draw a map layer for all
 * of a vessel's candidate ports. Syncing is idempotent — keyed by locode — so
 * re-inspecting a candidate or remounting does not duplicate map features.
 */
const useNextPortDestination = (
  nextport: NextPortArrivalWithDate,
  setPorts: NextPortItemProps['setPorts'],
) => {
  const { data: port, isLoading } = usePortGet(
    nextport?.locode ? nextport.locode : '',
  );

  useEffect(() => {
    if (!port) {
      return;
    }
    setPorts((prev) =>
      prev.some((existing) => existing.locode === port.locode)
        ? prev
        : [...prev, port],
    );
  }, [port, setPorts]);

  const country = nextport.locode ? locodeToCountry(nextport.locode) : null;

  return { port, isLoading, country };
};

/**
 * Resolves display context (port name + country) for the vessel's current port
 * call, used to explain the in-port no-prediction state. Read-only — unlike
 * {@link useNextPortDestination} it does not register a map layer.
 */
const useCurrentPortCallContext = (
  record: NextPortArrivalWithDate | undefined,
) => {
  const locode = record?.locode ?? '';
  const { data: port } = usePortGet(locode);

  return {
    locode: locode || undefined,
    portName: port?.name,
    country: locode ? locodeToCountry(locode) : null,
    ata: record?.ata,
  };
};

const useZoomToPort = (port: PortExtended | undefined) => {
  const { map } = useOpenLayersContext();

  return useCallback(() => {
    if (!port || !port.position) {
      return;
    }
    const transformedCoordinates = fromLonLat(
      port.position.coordinates as [number, number],
    );
    map.getView().setCenter(transformedCoordinates);
    map.getView().setZoom(Math.min(map.getView().getZoom() || 16));
  }, [map, port]);
};

interface NextPortDetailCardProps extends NextPortItemProps {
  /** e.g. "Most likely" for rank 1, or "Rank 4 of 7" otherwise. */
  rankLabel: string;
}

/** The currently inspected candidate, shown as a highlighted detail card. */
const NextPortDetailCard = ({
  shipInfo,
  nextport,
  setPorts,
  rankLabel,
}: NextPortDetailCardProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const etaCountdown = useTimeUntil(nextport.eta);
  const { port, isLoading, country } = useNextPortDestination(
    nextport,
    setPorts,
  );
  const onZoomToPort = useZoomToPort(port);
  const { isRouteShown, toggleRoute, isCalculatingRoute, canDrawRoute } =
    useDrawRouteToPort({
      mmsi: shipInfo.mmsi,
      shipName: shipInfo.name,
      nextport,
      portName: port?.name,
    });

  if (isLoading) {
    return <LoadingSpinner size="small"></LoadingSpinner>;
  }

  const probabilityPercent = nextport.probability
    ? nextport.probability * 100
    : 0;

  return (
    <div className="rounded-lg border bg-muted/40 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            {rankLabel}
          </p>
          <p className="mt-0.5 truncate text-base font-semibold">
            {port ? port.name : '-'}
          </p>
          <p className="truncate text-sm text-muted-foreground">
            {nextport.locode}
            {country && ` · ${country}`}
          </p>
        </div>
        <p className="shrink-0 text-xl font-bold">
          {formatProbability(nextport.probability)}
        </p>
      </div>

      <Progress value={probabilityPercent} className="mt-2 h-1.5" />

      <Separator className="my-3" />

      <div className="flex items-center justify-between gap-2 text-sm">
        <div className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
          <PiClock className="size-4 shrink-0" />
          <span className="truncate">
            ETA {formatDateTimeWithTimeZone(nextport.eta, true)}
            {etaCountdown && ` · ${etaCountdown}`}
          </span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="shrink-0 rounded-md border p-1 text-foreground hover:bg-accent"
              aria-label="Next port actions"
            >
              <PiDotsThreeVertical className="size-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onZoomToPort}>
              <PiMagnifyingGlass className="mr-2 size-4" />
              Zoom to port
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={toggleRoute}
              disabled={isCalculatingRoute || !canDrawRoute}
            >
              <PiPath className="mr-2 size-4" />
              {isRouteShown ? 'Remove path to port' : 'Draw path to port'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

interface NextPortCandidateRowProps extends NextPortItemProps {
  isSelected: boolean;
  onSelect: () => void;
}

/** A single candidate, shown as a clickable list row that inspects it in the detail card above. */
const NextPortCandidateRow = ({
  nextport,
  setPorts,
  isSelected,
  onSelect,
}: NextPortCandidateRowProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { port, isLoading } = useNextPortDestination(nextport, setPorts);

  if (isLoading) {
    return <LoadingSpinner size="small"></LoadingSpinner>;
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-accent/60',
        isSelected && 'bg-accent/60',
      )}
    >
      <span className={probabilityDotClass(nextport.probability)}></span>
      <span className="w-14 shrink-0 tabular-nums text-muted-foreground">
        {formatProbability(nextport.probability)}
      </span>
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span
          className={cn(
            'truncate font-medium',
            isSelected ? 'text-primary' : 'text-foreground',
          )}
        >
          {port ? port.name : '-'}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {nextport.locode}
        </span>
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {formatDateTimeWithTimeZone(nextport.eta, true)}
      </span>
    </button>
  );
};

export const NextPortPanel = ({ shipInfo }: NextPortProps) => {
  const { messages } = useLocalization();
  const { addLayer, doesLayerExist, zoomToLayer, getLayerById } = useLayers();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const { settings } = useSettings();
  const inclAnchorages =
    settings['ribbonPlugin.settings']?.nextport_incl_anchorages ?? false;

  const { data, isLoading, isError } = useNextportShipImoGet(
    shipInfo.imo,
    inclAnchorages,
  );
  const [ports, setPorts] = React.useState<PortExtended[]>([]);
  const [selectedLocode, setSelectedLocode] = useState<string | undefined>(
    undefined,
  );

  const records = data?.records ?? [];
  const currentPortCall = records.find(isCurrentPortCall);
  const currentPortCallContext = useCurrentPortCallContext(currentPortCall);

  // Only the ship endpoint returns current-port-call records; the port-level
  // arrivals call never does. Still, filtering keeps the panel honest about
  // what a "candidate" is if the payload ever carries both.
  const candidates = records.filter(isNextPortPrediction);
  const hasCandidates = candidates.length > 0;

  const id = useMemo(() => `ports-nextport-${shipInfo.mmsi}`, [shipInfo.mmsi]);
  const name = useMemo(() => `${shipInfo.name} nextports`, [shipInfo.name]);

  // Drop any ports left over from a previous response once the vessel has no
  // predictions (e.g. it just entered port). Without this the ports-nextport-*
  // layer keeps showing stale predicted ports under the no-prediction card.
  useEffect(() => {
    if (!hasCandidates) {
      setPorts([]);
    }
  }, [hasCandidates]);

  useEffect(() => {
    const layer = getLayerById(id);

    if (layer) {
      const source = (layer.layer as VectorLayer<Feature>).getSource();

      // Ensure source is a VectorSource before modifying
      if (source && source instanceof VectorSource) {
        source.clear();
        const newFeatures = ports
          .filter((port) => port.position && port.position.coordinates)
          .map((port) => {
            return new Feature({
              geometry: new Point(
                fromLonLat(
                  (port.position as Geometry).coordinates as [number, number],
                ),
              ),
              id: port?.id ? port.id.toString() : 'unknown port',
              name: port.name,
              locode: port.locode,
            });
          });

        // Add new features to the existing source
        source.addFeatures(newFeatures);
      }
    }
  }, [getLayerById, id, ports]);

  useEffect(() => {
    if (ports.length > 0 && !doesLayerExist(id)) {
      const portsGeoJson: GeoJson = {
        type: 'FeatureCollection',
        properties: {
          id: id,
          name: name,
        },
        features: [],
      };
      const layer = shapeToVectorLayer(
        portsGeoJson,
        displayProjection,
        dataProjection,
      );

      addLayer(layer, name, true, true, true, undefined, id);
    }
  }, [
    addLayer,
    doesLayerExist,
    id,
    name,
    ports,
    displayProjection,
    dataProjection,
  ]);

  if (isLoading) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  // Derive the inspected candidate from selection state, falling back to the
  // top (most likely) candidate — no effect needed to keep this in sync.
  // Candidates are keyed by locode (unique per port), not `id` — that field
  // is the trip id and is shared across every candidate for a vessel.
  const selectedIndex = Math.max(
    0,
    candidates.findIndex((candidate) => candidate.locode === selectedLocode),
  );
  const selectedCandidate = candidates[selectedIndex];

  const rankLabel =
    selectedIndex === 0
      ? 'Most likely'
      : `Rank ${selectedIndex + 1} of ${candidates.length}`;

  const rawReason = data?.reason;
  const reason = rawReason ? resolveNoPredictionReason(rawReason) : null;

  return (
    <div className="grid gap-6">
      <div className="grid gap-4">
        <div>
          <div className="mb-4">
            <h3 className="text-lg font-medium">Next port</h3>
            <p className="text-xs text-muted-foreground">
              {inclAnchorages
                ? 'Includes anchorage areas'
                : 'Excludes anchorage areas'}
            </p>
          </div>

          {selectedCandidate && (
            <NextPortDetailCard
              key={selectedCandidate.locode}
              shipInfo={shipInfo}
              nextport={selectedCandidate}
              setPorts={setPorts}
              rankLabel={rankLabel}
            />
          )}

          {candidates.length === 0 && reason && rawReason && (
            <NextPortNoPredictionCard
              reason={reason}
              rawReason={rawReason}
              context={currentPortCall ? currentPortCallContext : undefined}
            />
          )}

          {candidates.length === 0 && !reason && (
            <EmptyListTable
              entity="next port predictions"
              srcImage="empty_list_bro.avif"
            />
          )}

          {candidates.length > 1 && (
            <div className="mt-4">
              <div className="flex items-center justify-between px-1 pb-1">
                <p className="text-xs text-muted-foreground">
                  All candidates · click to inspect
                </p>
                <p className="text-xs text-muted-foreground">ETA</p>
              </div>
              <div className="divide-y rounded-lg border shadow-xs">
                {candidates.map((nextport) => (
                  <NextPortCandidateRow
                    key={nextport.locode}
                    shipInfo={shipInfo}
                    nextport={nextport}
                    setPorts={setPorts}
                    isSelected={nextport.locode === selectedCandidate?.locode}
                    onSelect={() => setSelectedLocode(nextport.locode)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
